import React, { useCallback, useEffect, useState } from 'react';
import { Switch } from '@headlessui/react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import DocumentViewer from '../../components/Documents/DocumentViewer';
import OpenDocumentViewer from '../../components/Documents/OpenDocumentViewer';
import { Button } from '../../components/UI/Button';
import { ConfirmModal } from '../../components/UI/ConfirmModal';
import { Modal } from '../../components/UI/Modal';
import { useToast } from '../../components/UI/ToastProvider';
import { documentDownloadService } from '../../services/documentDownloadService';
import { formService } from '../../services/formService';
import type { DocumentNode } from '../../types/documents';
import {
  FIELD_TYPES_WITH_BRANCHING,
  FIELD_TYPES_WITH_OPTIONS,
  FORM_COLLECT_EMAIL_LABELS,
  FORM_PURPOSE_LABELS,
  FORM_PURPOSE_OPTIONS,
  FORM_STATUS_LABELS,
  DEFAULT_CONFIRMATION_MESSAGE,
  createDefaultFormTheme,
  isDisplayOnlyFieldType,
  resolveFormTheme,
} from '../../types/form';
import type {
  Form,
  FormAccessType,
  FormCollectEmail,
  FormField,
  FormNavigationAction,
  FormPurpose,
  FormResponse,
  FormResponseAnswer,
  FormSection,
  FormStatus,
  FormTheme,
} from '../../types/form';
import { getSectionFields, sortSections } from './formNavigation';
import {
  FORM_FIELD_CONTACT_BINDING_OPTIONS,
  FORM_FIELD_MASK_OPTIONS,
  RATING_ICON_OPTIONS,
  RATING_SCALE_OPTIONS,
  alignmentClass,
  buildContactBindingSettingsJson,
  buildImageBlockSettingsJson,
  buildMaskSettingsJson,
  buildRatingSettingsJson,
  buildVideoBlockSettingsJson,
  buildYoutubeEmbedUrl,
  extractYoutubeVideoId,
  formHasContactBindings,
  patchInlineImageSettings,
  resolveContactBinding,
  resolveDisplayImageUrl,
  resolveImageBlockSettings,
  resolveInlineImageSettings,
  resolveMaskSettings,
  resolveRatingSettings,
  resolveVideoBlockSettings,
  stripContactBindingsFromFields,
  type FormFieldContactBinding,
  type FormFieldMaskKind,
  type FormImageAlignment,
  type FormRatingIcon,
} from '../../types/formFieldSettings';
import { FormFieldTypeSelect } from './FormFieldTypeSelect';
import { FormInsertImageModal } from './FormInsertImageModal';
import type { FormImageInsertResult } from './FormInsertImageModal';
import { FormSelectVideoModal } from './FormSelectVideoModal';
import { FormRatingIconGlyph, FormRatingScale } from './FormRatingScale';
import { FormThemeEditor } from './FormThemeEditor';
import { FormPreviewModal } from './FormPreviewModal';
import {
  buildFormThemeCssVars,
  ensureFormThemeFontsLoaded,
} from './FormThemePreview';

type BuilderTab = 'perguntas' | 'respostas' | 'configuracoes';
type AttachmentViewerKind = 'document' | 'opendocument' | 'unsupported';
type MediaModalTarget =
  | { kind: 'block'; fieldId: string }
  | { kind: 'inline'; fieldId: string }
  | { kind: 'new-image' }
  | null;

function formatDateTime(value: string): string {
  try {
    return new Date(value).toLocaleString('pt-BR');
  } catch {
    return value;
  }
}

function getApiErrorMessage(error: unknown, fallback: string): string {
  const response = (
    error as {
      response?: { data?: { message?: string; error?: string; title?: string; detail?: string } };
    }
  )?.response?.data;

  if (response?.error) {
    return response.error;
  }
  if (response?.message) {
    return response.message;
  }
  if (response?.detail) {
    return response.detail;
  }
  if (response?.title) {
    return response.title;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return fallback;
}

function resolveAttachmentFileName(answer: FormResponseAnswer): string {
  return answer.fileName || answer.valueText || 'arquivo';
}

function getAttachmentFileExtension(fileName: string): string {
  return fileName.split('.').pop()?.toLowerCase() || '';
}

function getAttachmentViewerKind(fileName: string, contentType?: string): AttachmentViewerKind {
  const ext = getAttachmentFileExtension(fileName);
  const type = (contentType ?? '').toLowerCase();

  if (['odt', 'odp', 'ods', 'odg'].includes(ext))
  {
    return 'opendocument';
  }

  if (
    ext === 'pdf'
    || type === 'application/pdf'
    || ['html', 'htm'].includes(ext)
    || ext === 'txt'
    || ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'].includes(ext)
    || type.startsWith('image/')
    || ['doc', 'docx'].includes(ext)
    || ['xls', 'xlsx'].includes(ext)
    || ext === 'edoc'
  )
  {
    return 'document';
  }

  return 'unsupported';
}

function buildAttachmentDocumentNode(answer: FormResponseAnswer): DocumentNode {
  const fileName = resolveAttachmentFileName(answer);
  const ext = getAttachmentFileExtension(fileName);
  const contentType = answer.contentType || 'application/octet-stream';
  const isPdf =
    ext === 'pdf' || contentType.toLowerCase() === 'application/pdf';

  return {
    id: answer.documentId!,
    customerId: '',
    title: fileName,
    description: '',
    nodeType: isPdf ? 'pdf' : 'file',
    parentDocumentId: null,
    level: 0,
    order: 0,
    children: [],
    countChild: 0,
    path: '',
    fullPath: '',
    status: '',
    currentVersion: null,
    content: null,
    metadata: {
      fileName,
      contentType,
      size: '',
      createdAt: '',
      updatedAt: '',
    },
  };
}

function formStatusBadgeClass(status: FormStatus): string {
  if (status === 'Published')
  {
    return 'bg-green-100 text-green-800 border-green-200';
  }

  if (status === 'Closed')
  {
    return 'bg-red-100 text-red-800 border-red-200';
  }

  return 'bg-gray-100 text-gray-800 border-gray-200';
}

function formPurposeBadgeClass(purpose: FormPurpose): string {
  if (purpose === 'Support')
  {
    return 'bg-blue-100 text-blue-800 border-blue-200';
  }

  return 'bg-gray-100 text-gray-700 border-gray-200';
}

const ACCESS_TYPE_OPTIONS: {
  value: FormAccessType;
  label: string;
  description: string;
  icon: React.ReactNode;
}[] = [
  {
    value: 'Public',
    label: 'Público',
    description: 'Qualquer pessoa com o link pode responder, sem login.',
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-5 w-5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        aria-hidden
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    ),
  },
  {
    value: 'Private',
    label: 'Privado',
    description: 'Somente usuários autenticados podem responder.',
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-5 w-5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        aria-hidden
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
        />
      </svg>
    ),
  },
];

function normalizeStructure(
  sections: FormSection[],
  fields: FormField[]
): { sections: FormSection[]; fields: FormField[] } {
  const sortedSections = sections
    .slice()
    .sort((a, b) => a.order - b.order)
    .map((section, index) => ({ ...section, order: index }));
  const validSectionIds = new Set(sortedSections.map((s) => s.id));

  const cleanedSections = sortedSections.map((section) => {
    if (
      section.afterSectionAction === 'GoToSection' &&
      (!section.afterSectionTargetId ||
        !validSectionIds.has(section.afterSectionTargetId))
    ) {
      return {
        ...section,
        afterSectionAction: 'ContinueToNext' as FormNavigationAction,
        afterSectionTargetId: null,
      };
    }
    return section;
  });

  const cleanField = (field: FormField, order: number): FormField => ({
    ...field,
    order,
    options: field.options.map((option) => {
      if (
        option.goToAction === 'GoToSection' &&
        (!option.goToTargetSectionId ||
          !validSectionIds.has(option.goToTargetSectionId))
      ) {
        return {
          ...option,
          goToAction: 'ContinueToNext' as FormNavigationAction,
          goToTargetSectionId: null,
        };
      }
      return option;
    }),
  });

  const orderedFields: FormField[] = [];
  let orderCounter = 0;
  const fallbackSectionId = cleanedSections[0]?.id;

  cleanedSections.forEach((section) => {
    fields
      .filter((field) => field.sectionId === section.id)
      .sort((a, b) => a.order - b.order)
      .forEach((field) => {
        orderedFields.push(cleanField(field, orderCounter));
        orderCounter += 1;
      });
  });

  if (fallbackSectionId) {
    fields
      .filter((field) => !validSectionIds.has(field.sectionId))
      .forEach((field) => {
        orderedFields.push(
          cleanField({ ...field, sectionId: fallbackSectionId }, orderCounter)
        );
        orderCounter += 1;
      });
  }

  return { sections: cleanedSections, fields: orderedFields };
}

function ensureOptions(field: FormField): FormField {
  if (field.type === 'Image' || field.type === 'Video')
  {
    return {
      ...field,
      options: [],
      isRequired: false,
      validationRegex: undefined,
      validationMessage: undefined,
      goToSectionBasedOnAnswer: false,
    };
  }

  if (field.type === 'Rating')
  {
    const rating = resolveRatingSettings(field);
    const current = field.settingsJson
      ? (() =>
        {
          try
          {
            return JSON.parse(field.settingsJson) as Record<string, unknown>;
          }
          catch
          {
            return {};
          }
        })()
      : {};
    const inlineImage = current.inlineImage;
    return {
      ...field,
      options: [],
      settingsJson: JSON.stringify({
        ratingScale: rating.scale,
        ratingIcon: rating.icon,
        ...(inlineImage ? { inlineImage } : {}),
      }),
    };
  }

  if (!FIELD_TYPES_WITH_OPTIONS.includes(field.type)) {
    return { ...field, options: [] };
  }
  if (field.options.length > 0) {
    return field;
  }
  return {
    ...field,
    options: [
      formService.createDefaultOption(0),
      formService.createDefaultOption(1),
    ],
  };
}

export const FormBuilderPage: React.FC = () => {
  const { formId } = useParams<{ formId: string }>();
  const navigate = useNavigate();
  const { addToast } = useToast();

  const [activeTab, setActiveTab] = useState<BuilderTab>('perguntas');
  const [form, setForm] = useState<Form | null>(null);
  const [responses, setResponses] = useState<FormResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [isPurposeModalOpen, setIsPurposeModalOpen] = useState(false);
  const [purposeDraft, setPurposeDraft] = useState<FormPurpose>('General');
  const [pendingPurposeChange, setPendingPurposeChange] = useState<FormPurpose | null>(
    null
  );
  const [isExporting, setIsExporting] = useState(false);
  const [attachmentActionKey, setAttachmentActionKey] = useState<string | null>(
    null
  );
  const [viewerDocument, setViewerDocument] = useState<DocumentNode | null>(null);
  const [showDocumentViewer, setShowDocumentViewer] = useState(false);
  const [showOpenDocumentViewer, setShowOpenDocumentViewer] = useState(false);
  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null);
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
  const [expandedValidationId, setExpandedValidationId] = useState<string | null>(
    null
  );
  const [isUploadingHeader, setIsUploadingHeader] = useState(false);
  const [isThemePanelOpen, setIsThemePanelOpen] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [imageModalTarget, setImageModalTarget] = useState<MediaModalTarget>(null);
  const [videoModalFieldId, setVideoModalFieldId] = useState<string | null>(null);
  const [isNewVideoModalOpen, setIsNewVideoModalOpen] = useState(false);

  const loadForm = useCallback(async () => {
    if (!formId) {
      return;
    }

    try {
      setIsLoading(true);
      const data = await formService.getById(formId);
      setForm(data);
      const firstSectionId = sortSections(data.sections)[0]?.id ?? null;
      setActiveSectionId(firstSectionId);
      if (data.fields.length > 0) {
        const firstField = getSectionFields(data.fields, firstSectionId ?? '')[0]
          ?? data.fields[0];
        setSelectedFieldId(firstField.id);
        setActiveSectionId(firstField.sectionId);
      }
      const responseData = await formService.getResponses(formId);
      setResponses(responseData);
    } catch {
      addToast('Formulário não encontrado.', 'error');
      navigate('/forms');
    } finally {
      setIsLoading(false);
    }
  }, [formId, addToast, navigate]);

  const loadResponses = useCallback(async () => {
    if (!formId) {
      return;
    }

    try {
      const data = await formService.getResponses(formId);
      setResponses(data);
    } catch {
      addToast('Erro ao carregar respostas.', 'error');
    }
  }, [formId, addToast]);

  useEffect(() => {
    loadForm();
  }, [loadForm]);

  useEffect(() => {
    ensureFormThemeFontsLoaded();
  }, []);

  useEffect(() => {
    if (activeTab === 'respostas') {
      loadResponses();
    }
  }, [activeTab, loadResponses]);

  const updateFormLocal = (patch: Partial<Form>) => {
    setForm((prev) => (prev ? { ...prev, ...patch } : prev));
  };

  const updateField = (fieldId: string, patch: Partial<FormField>) => {
    setForm((prev) => {
      if (!prev) {
        return prev;
      }
      const fields = prev.fields.map((field) => {
        if (field.id !== fieldId) {
          return field;
        }
        return ensureOptions({ ...field, ...patch });
      });
      return { ...prev, fields };
    });
  };

  const handleSave = async () => {
    if (!form) {
      return;
    }

    try {
      setIsSaving(true);
      const updated = await formService.update(form.id, {
        title: form.title,
        description: form.description,
        status: form.status,
        accessType: form.accessType,
        acceptingResponses:
          form.status === 'Closed' ? false : form.acceptingResponses,
        theme: resolveFormTheme(form.theme),
        sections: form.sections,
        fields: form.fields,
      });
      setForm(updated);
      addToast('Formulário salvo.', 'success');
    } catch {
      addToast('Erro ao salvar formulário.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleThemeChange = (theme: FormTheme) => {
    updateFormLocal({ theme: resolveFormTheme(theme) });
  };

  const handleUploadThemeHeader = async (file: File) => {
    if (!form)
    {
      return;
    }

    try
    {
      setIsUploadingHeader(true);
      const updated = await formService.uploadThemeHeader(form.id, file);
      setForm((prev) =>
      {
        if (!prev)
        {
          return prev;
        }

        return {
          ...prev,
          theme: resolveFormTheme(updated.theme),
          updatedAt: updated.updatedAt,
        };
      });
      addToast('Imagem de cabeçalho atualizada.', 'success');
    }
    catch
    {
      addToast('Erro ao enviar imagem de cabeçalho.', 'error');
      throw new Error('upload_failed');
    }
    finally
    {
      setIsUploadingHeader(false);
    }
  };

  const handleRemoveThemeHeader = async () => {
    if (!form)
    {
      return;
    }

    try
    {
      setIsUploadingHeader(true);
      const updated = await formService.deleteThemeHeader(form.id);
      setForm((prev) =>
      {
        if (!prev)
        {
          return prev;
        }

        return {
          ...prev,
          theme: resolveFormTheme(updated.theme),
          updatedAt: updated.updatedAt,
        };
      });
      addToast('Imagem de cabeçalho removida.', 'success');
    }
    catch
    {
      addToast('Erro ao remover imagem de cabeçalho.', 'error');
    }
    finally
    {
      setIsUploadingHeader(false);
    }
  };

  const handleRestoreDefaultTheme = () => {
    updateFormLocal({ theme: createDefaultFormTheme() });
  };

  const handleAccessTypeChange = async (accessType: FormAccessType) => {
    if (!form)
    {
      return;
    }

    if (form.status === 'Closed' || isSavingSettings || form.accessType === accessType)
    {
      return;
    }

    const previousAccessType = form.accessType;
    updateFormLocal({ accessType });

    try
    {
      setIsSavingSettings(true);
      const updated = await formService.update(form.id, {
        title: form.title,
        description: form.description,
        accessType,
        limitOneResponsePerUser:
          accessType === 'Public' ? false : undefined,
      });
      setForm((prev) =>
      {
        if (!prev)
        {
          return prev;
        }

        return {
          ...prev,
          accessType: updated.accessType,
          limitOneResponsePerUser: updated.limitOneResponsePerUser,
          updatedAt: updated.updatedAt,
        };
      });
      const label = accessType === 'Public' ? 'Público' : 'Privado';
      addToast(`Acesso alterado para ${label}.`, 'success');
    }
    catch
    {
      updateFormLocal({ accessType: previousAccessType });
      addToast('Erro ao alterar tipo de acesso.', 'error');
    }
    finally
    {
      setIsSavingSettings(false);
    }
  };

  const applyPurposeChange = async (purpose: FormPurpose) => {
    if (!form)
    {
      return;
    }

    const previousPurpose = form.purpose || 'General';
    const previousFields = form.fields;
    const shouldStripBindings =
      purpose === 'General' && formHasContactBindings(form.fields);
    updateFormLocal({
      purpose,
      ...(shouldStripBindings
        ? { fields: stripContactBindingsFromFields(form.fields) }
        : {}),
    });

    try
    {
      setIsSavingSettings(true);
      const updated = await formService.update(form.id, {
        title: form.title,
        description: form.description,
        purpose,
      });
      setForm((prev) =>
      {
        if (!prev)
        {
          return prev;
        }

        return {
          ...prev,
          purpose: updated.purpose || 'General',
          fields: updated.fields?.length
            ? updated.fields
            : shouldStripBindings
              ? stripContactBindingsFromFields(prev.fields)
              : prev.fields,
          updatedAt: updated.updatedAt,
        };
      });
      addToast(
        shouldStripBindings
          ? `Finalidade alterada para ${FORM_PURPOSE_LABELS[purpose]}. Vínculos com contato removidos.`
          : `Finalidade alterada para ${FORM_PURPOSE_LABELS[purpose]}.`,
        'success'
      );
    }
    catch
    {
      updateFormLocal({ purpose: previousPurpose, fields: previousFields });
      addToast('Erro ao alterar finalidade.', 'error');
    }
    finally
    {
      setIsSavingSettings(false);
      setPendingPurposeChange(null);
      setIsPurposeModalOpen(false);
    }
  };

  const openPurposeModal = () => {
    if (!form || form.status === 'Closed' || isSavingSettings)
    {
      return;
    }

    setPurposeDraft(form.purpose || 'General');
    setIsPurposeModalOpen(true);
  };

  const handlePurposeChange = (purpose: FormPurpose) => {
    if (!form)
    {
      return;
    }

    if (form.status === 'Closed' || isSavingSettings || (form.purpose || 'General') === purpose)
    {
      setIsPurposeModalOpen(false);
      return;
    }

    setIsPurposeModalOpen(false);

    if (purpose === 'General' && formHasContactBindings(form.fields))
    {
      setPendingPurposeChange(purpose);
      return;
    }

    void applyPurposeChange(purpose);
  };

  const handleAcceptingResponsesChange = async (checked: boolean) => {
    if (!form)
    {
      return;
    }

    if (form.status === 'Closed' || isSavingSettings)
    {
      return;
    }

    const previousAcceptingResponses = form.acceptingResponses;
    updateFormLocal({ acceptingResponses: checked });

    try
    {
      setIsSavingSettings(true);
      const updated = await formService.update(form.id, {
        title: form.title,
        description: form.description,
        acceptingResponses: checked,
      });
      setForm((prev) =>
      {
        if (!prev)
        {
          return prev;
        }

        return {
          ...prev,
          acceptingResponses: updated.acceptingResponses,
          updatedAt: updated.updatedAt,
        };
      });
      addToast(
        checked ? 'Formulário aceitando respostas.' : 'Recebimento de respostas pausado.',
        'success'
      );
    }
    catch
    {
      updateFormLocal({ acceptingResponses: previousAcceptingResponses });
      addToast('Erro ao alterar recebimento de respostas.', 'error');
    }
    finally
    {
      setIsSavingSettings(false);
    }
  };

  const handleSettingPatch = async (
    patch: Partial<Form>,
    successMessage: string
  ) => {
    if (!form || form.status === 'Closed' || isSavingSettings)
    {
      return;
    }

    const previous = { ...form };
    updateFormLocal(patch);

    try
    {
      setIsSavingSettings(true);

      const request: Parameters<typeof formService.update>[1] = {
        title: form.title,
        description: form.description,
      };

      if (patch.collectEmail !== undefined)
      {
        request.collectEmail = patch.collectEmail;
      }
      if (patch.limitOneResponsePerUser !== undefined)
      {
        request.limitOneResponsePerUser = patch.limitOneResponsePerUser;
      }
      if (patch.showProgressBar !== undefined)
      {
        request.showProgressBar = patch.showProgressBar;
      }
      if (patch.confirmationMessage !== undefined)
      {
        request.confirmationMessage = patch.confirmationMessage || null;
      }
      if (patch.showSubmitAnotherResponseLink !== undefined)
      {
        request.showSubmitAnotherResponseLink = patch.showSubmitAnotherResponseLink;
      }
      if (patch.allowResponseEditing !== undefined)
      {
        request.allowResponseEditing = patch.allowResponseEditing;
      }
      if (patch.responseEditingDeadlineDays !== undefined)
      {
        request.responseEditingDeadlineDays = patch.responseEditingDeadlineDays;
        request.clearResponseEditingDeadlineDays =
          patch.responseEditingDeadlineDays === null;
      }
      if (patch.allowDraftResponses !== undefined)
      {
        request.allowDraftResponses = patch.allowDraftResponses;
      }
      if (patch.sendResponseCopyToRespondent !== undefined)
      {
        request.sendResponseCopyToRespondent = patch.sendResponseCopyToRespondent;
      }
      if (patch.fieldsRequiredByDefault !== undefined)
      {
        request.fieldsRequiredByDefault = patch.fieldsRequiredByDefault;
      }

      const updated = await formService.update(form.id, request);
      setForm((prev) =>
      {
        if (!prev)
        {
          return prev;
        }

        return {
          ...prev,
          ...normalizeSettingsFromForm(updated),
          ...Object.fromEntries(
            Object.entries(patch).filter(([, value]) => value !== undefined)
          ),
          updatedAt: updated.updatedAt,
        };
      });
      addToast(successMessage, 'success');
    }
    catch (error: unknown)
    {
      setForm(previous);
      addToast(
        getApiErrorMessage(error, 'Erro ao atualizar configuração.'),
        'error'
      );
    }
    finally
    {
      setIsSavingSettings(false);
    }
  };

  const normalizeSettingsFromForm = (source: Form): Partial<Form> => ({
    collectEmail: source.collectEmail,
    limitOneResponsePerUser: source.limitOneResponsePerUser,
    showProgressBar: source.showProgressBar,
    confirmationMessage: source.confirmationMessage,
    showSubmitAnotherResponseLink: source.showSubmitAnotherResponseLink,
    allowResponseEditing: source.allowResponseEditing,
    responseEditingDeadlineDays: source.responseEditingDeadlineDays,
    allowDraftResponses: source.allowDraftResponses,
    sendResponseCopyToRespondent: source.sendResponseCopyToRespondent,
    fieldsRequiredByDefault: source.fieldsRequiredByDefault,
  });

  const handlePublish = async () => {
    if (!form) {
      return;
    }

    try {
      setIsSaving(true);
      await formService.update(form.id, {
        title: form.title,
        description: form.description,
        status: form.status,
        accessType: form.accessType,
        acceptingResponses: form.acceptingResponses,
        sections: form.sections,
        fields: form.fields,
      });
      const published = await formService.publish(form.id);
      setForm(published);
      addToast('Formulário publicado.', 'success');
    } catch {
      addToast('Erro ao publicar formulário.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = async () => {
    if (!form) {
      return;
    }

    try {
      setIsSaving(true);
      const closed = await formService.close(form.id);
      setForm(closed);
      addToast('Formulário encerrado.', 'success');
    } catch {
      addToast('Erro ao encerrar formulário.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const resolveActiveSectionId = (source: Form): string | null => {
    if (activeSectionId && source.sections.some((s) => s.id === activeSectionId)) {
      return activeSectionId;
    }

    const selectedSectionId = selectedFieldId
      ? source.fields.find((f) => f.id === selectedFieldId)?.sectionId
      : undefined;
    if (selectedSectionId && source.sections.some((s) => s.id === selectedSectionId)) {
      return selectedSectionId;
    }

    return sortSections(source.sections)[0]?.id ?? null;
  };

  const handleAddField = () => {
    if (!form) {
      return;
    }

    const sectionId = resolveActiveSectionId(form);
    if (!sectionId) {
      return;
    }

    const newField = formService.createEmptyField(
      sectionId,
      form.fields.length,
      form.fieldsRequiredByDefault
    );
    const { sections, fields } = normalizeStructure(form.sections, [
      ...form.fields,
      newField,
    ]);
    setForm({ ...form, sections, fields });
    setSelectedFieldId(newField.id);
    setActiveSectionId(sectionId);
  };

  const handleAddMediaField = (type: 'Image' | 'Video') => {
    if (!form) {
      return;
    }

    const sectionId = resolveActiveSectionId(form);
    if (!sectionId) {
      return;
    }

    const newField = formService.createEmptyMediaField(
      type,
      sectionId,
      form.fields.length
    );
    const { sections, fields } = normalizeStructure(form.sections, [
      ...form.fields,
      newField,
    ]);
    setForm({ ...form, sections, fields });
    setSelectedFieldId(newField.id);
    setActiveSectionId(sectionId);

    if (type === 'Image')
    {
      setImageModalTarget({ kind: 'block', fieldId: newField.id });
    }
    else
    {
      setVideoModalFieldId(newField.id);
    }
  };

  const applyImageToField = (
    fieldId: string,
    result: FormImageInsertResult,
    mode: 'block' | 'inline'
  ) => {
    if (mode === 'block')
    {
      updateField(fieldId, {
        settingsJson: buildImageBlockSettingsJson({
          imageSource: result.imageSource,
          objectKey: result.objectKey,
          externalUrl: result.externalUrl,
          alignment: 'Center',
        }),
        mediaUrl: result.previewUrl,
      });
      return;
    }

    setForm((prev) => {
      if (!prev)
      {
        return prev;
      }

      return {
        ...prev,
        fields: prev.fields.map((field) =>
        {
          if (field.id !== fieldId)
          {
            return field;
          }

          return ensureOptions({
            ...field,
            settingsJson: patchInlineImageSettings(field.settingsJson, {
              imageSource: result.imageSource,
              objectKey: result.objectKey,
              externalUrl: result.externalUrl,
              alignment: 'Center',
            }),
            inlineImageUrl: result.previewUrl,
          });
        }),
      };
    });
  };

  const handleImageModalConfirm = (result: FormImageInsertResult) => {
    if (!imageModalTarget)
    {
      return;
    }

    if (imageModalTarget.kind === 'new-image')
    {
      if (!form)
      {
        return;
      }

      const sectionId = resolveActiveSectionId(form);
      if (!sectionId)
      {
        return;
      }

      const newField = formService.createEmptyMediaField(
        'Image',
        sectionId,
        form.fields.length
      );
      newField.settingsJson = buildImageBlockSettingsJson({
        imageSource: result.imageSource,
        objectKey: result.objectKey,
        externalUrl: result.externalUrl,
        alignment: 'Center',
      });
      newField.mediaUrl = result.previewUrl;

      const { sections, fields } = normalizeStructure(form.sections, [
        ...form.fields,
        newField,
      ]);
      setForm({ ...form, sections, fields });
      setSelectedFieldId(newField.id);
      setActiveSectionId(sectionId);
      setImageModalTarget(null);
      return;
    }

    applyImageToField(
      imageModalTarget.fieldId,
      result,
      imageModalTarget.kind === 'inline' ? 'inline' : 'block'
    );
    setImageModalTarget(null);
  };

  const handleVideoModalConfirm = (youtubeUrl: string, fieldId: string | null) => {
    if (fieldId)
    {
      updateField(fieldId, {
        settingsJson: buildVideoBlockSettingsJson(youtubeUrl),
        mediaUrl: youtubeUrl,
      });
      setVideoModalFieldId(null);
      return;
    }

    if (!form)
    {
      return;
    }

    const sectionId = resolveActiveSectionId(form);
    if (!sectionId)
    {
      return;
    }

    const newField = formService.createEmptyMediaField(
      'Video',
      sectionId,
      form.fields.length
    );
    newField.settingsJson = buildVideoBlockSettingsJson(youtubeUrl);
    newField.mediaUrl = youtubeUrl;

    const { sections, fields } = normalizeStructure(form.sections, [
      ...form.fields,
      newField,
    ]);
    setForm({ ...form, sections, fields });
    setSelectedFieldId(newField.id);
    setActiveSectionId(sectionId);
    setIsNewVideoModalOpen(false);
  };

  const handleAddSection = () => {
    if (!form) {
      return;
    }

    const sorted = sortSections(form.sections);
    const activeId = resolveActiveSectionId(form);
    const activeIndex = sorted.findIndex((s) => s.id === activeId);
    const anchorIndex = activeIndex >= 0 ? activeIndex : sorted.length - 1;
    const anchorOrder = sorted[anchorIndex]?.order ?? sorted.length - 1;

    const newSection: FormSection = {
      ...formService.createEmptySection(0),
      order: anchorOrder + 0.5,
    };

    const { sections, fields } = normalizeStructure(
      [...form.sections, newSection],
      form.fields
    );
    setForm({ ...form, sections, fields });
    setActiveSectionId(newSection.id);
    setSelectedFieldId(null);
  };

  const handleAddFieldToSection = (sectionId: string) => {
    if (!form) {
      return;
    }

    const newField = formService.createEmptyField(
      sectionId,
      form.fields.length,
      form.fieldsRequiredByDefault
    );
    const { sections, fields } = normalizeStructure(form.sections, [
      ...form.fields,
      newField,
    ]);
    setForm({ ...form, sections, fields });
    setSelectedFieldId(newField.id);
    setActiveSectionId(sectionId);
  };

  const handleUpdateSection = (sectionId: string, patch: Partial<FormSection>) => {
    setForm((prev) => {
      if (!prev) {
        return prev;
      }
      return {
        ...prev,
        sections: prev.sections.map((section) =>
          section.id === sectionId ? { ...section, ...patch } : section
        ),
      };
    });
  };

  const handleSectionNavChange = (
    sectionId: string,
    action: FormNavigationAction,
    targetId: string | null
  ) => {
    handleUpdateSection(sectionId, {
      afterSectionAction: action,
      afterSectionTargetId: action === 'GoToSection' ? targetId : null,
    });
  };

  const handleDeleteSection = (sectionId: string) => {
    if (!form) {
      return;
    }
    if (form.sections.length <= 1) {
      addToast('O formulário precisa ter ao menos uma seção.', 'error');
      return;
    }

    const sorted = sortSections(form.sections);
    const index = sorted.findIndex((s) => s.id === sectionId);
    const fallback = sorted[index - 1] ?? sorted[index + 1];
    if (!fallback) {
      return;
    }

    const movedFields = form.fields.map((field) =>
      field.sectionId === sectionId
        ? { ...field, sectionId: fallback.id }
        : field
    );
    const remainingSections = form.sections.filter((s) => s.id !== sectionId);
    const { sections, fields } = normalizeStructure(remainingSections, movedFields);
    setForm({ ...form, sections, fields });
    setActiveSectionId(fallback.id);
  };

  const handleDuplicateField = (fieldId: string) => {
    if (!form) {
      return;
    }

    const source = form.fields.find((f) => f.id === fieldId);
    if (!source) {
      return;
    }

    const duplicated: FormField = {
      ...JSON.parse(JSON.stringify(source)),
      id: formService.createId(),
      order: source.order + 0.5,
      options: source.options.map((opt) => ({
        ...opt,
        id: formService.createId(),
      })),
    };

    const { sections, fields } = normalizeStructure(form.sections, [
      ...form.fields,
      duplicated,
    ]);
    setForm({ ...form, sections, fields });
    setSelectedFieldId(duplicated.id);
  };

  const handleDeleteField = (fieldId: string) => {
    if (!form) {
      return;
    }
    if (form.fields.length <= 1) {
      addToast('O formulário precisa ter ao menos uma pergunta.', 'error');
      return;
    }

    const remaining = form.fields.filter((f) => f.id !== fieldId);
    const { sections, fields } = normalizeStructure(form.sections, remaining);
    setForm({ ...form, sections, fields });
    setSelectedFieldId(fields[0]?.id ?? null);
  };

  const handleMoveField = (fieldId: string, direction: -1 | 1) => {
    if (!form) {
      return;
    }

    const field = form.fields.find((f) => f.id === fieldId);
    if (!field) {
      return;
    }

    const sectionFields = getSectionFields(form.fields, field.sectionId);
    const withinIndex = sectionFields.findIndex((f) => f.id === fieldId);
    const targetWithin = withinIndex + direction;

    if (targetWithin >= 0 && targetWithin < sectionFields.length) {
      const reordered = [...sectionFields];
      const [item] = reordered.splice(withinIndex, 1);
      reordered.splice(targetWithin, 0, item);
      const otherFields = form.fields.filter(
        (f) => f.sectionId !== field.sectionId
      );
      const rebuilt = [
        ...otherFields,
        ...reordered.map((f, i) => ({ ...f, order: i })),
      ];
      const { sections, fields } = normalizeStructure(form.sections, rebuilt);
      setForm({ ...form, sections, fields });
      return;
    }

    const sorted = sortSections(form.sections);
    const sectionIndex = sorted.findIndex((s) => s.id === field.sectionId);
    const targetSection =
      direction === -1 ? sorted[sectionIndex - 1] : sorted[sectionIndex + 1];
    if (!targetSection) {
      return;
    }

    const targetFields = getSectionFields(form.fields, targetSection.id);
    const movedField = { ...field, sectionId: targetSection.id };
    const newTargetFields =
      direction === -1
        ? [...targetFields, movedField]
        : [movedField, ...targetFields];
    const rest = form.fields.filter(
      (f) => f.id !== fieldId && f.sectionId !== targetSection.id
    );
    const rebuilt = [
      ...rest,
      ...newTargetFields.map((f, i) => ({ ...f, order: i })),
    ];
    const { sections, fields } = normalizeStructure(form.sections, rebuilt);
    setForm({ ...form, sections, fields });
    setActiveSectionId(targetSection.id);
  };

  const handleToggleFieldBranching = (fieldId: string, enabled: boolean) => {
    updateField(fieldId, { goToSectionBasedOnAnswer: enabled });
  };

  const handleOptionNavChange = (
    fieldId: string,
    optionId: string,
    action: FormNavigationAction,
    targetId: string | null
  ) => {
    updateField(fieldId, {
      options:
        form?.fields
          .find((f) => f.id === fieldId)
          ?.options.map((opt) =>
            opt.id === optionId
              ? {
                  ...opt,
                  goToAction: action,
                  goToTargetSectionId:
                    action === 'GoToSection' ? targetId : null,
                }
              : opt
          ) ?? [],
    });
  };

  const handleAddOption = (fieldId: string) => {
    setForm((prev) => {
      if (!prev) {
        return prev;
      }
      return {
        ...prev,
        fields: prev.fields.map((field) => {
          if (field.id !== fieldId) {
            return field;
          }
          return {
            ...field,
            options: [
              ...field.options,
              formService.createDefaultOption(field.options.length),
            ],
          };
        }),
      };
    });
  };

  const handleOptionLabelChange = (
    fieldId: string,
    optionId: string,
    label: string
  ) => {
    updateField(fieldId, {
      options:
        form?.fields
          .find((f) => f.id === fieldId)
          ?.options.map((opt) =>
            opt.id === optionId ? { ...opt, label } : opt
          ) ?? [],
    });
  };

  const handleRemoveOption = (fieldId: string, optionId: string) => {
    setForm((prev) => {
      if (!prev) {
        return prev;
      }
      return {
        ...prev,
        fields: prev.fields.map((field) => {
          if (field.id !== fieldId) {
            return field;
          }
          if (field.options.length <= 1) {
            return field;
          }
          return {
            ...field,
            options: field.options
              .filter((opt) => opt.id !== optionId)
              .map((opt, i) => ({ ...opt, order: i })),
          };
        }),
      };
    });
  };

  const handleCopyPublicLink = async () => {
    if (!form) {
      return;
    }

    const url = `${window.location.origin}/forms/public/${form.publicId}`;
    try {
      await navigator.clipboard.writeText(url);
      addToast('Link público copiado.', 'success');
    } catch {
      addToast(url, 'info');
    }
  };

  const handleExport = async () => {
    if (!form) {
      return;
    }

    try {
      setIsExporting(true);
      const result = await formService.exportToExcel(form.id);
      addToast(`Planilha gerada: ${result.fileName}`, 'success');
      navigate(`/documents/${result.documentId}/details`);
    } catch {
      addToast('Erro ao exportar respostas.', 'error');
    } finally {
      setIsExporting(false);
    }
  };

  const handleDownloadAttachment = async (
    answer: FormResponseAnswer,
    actionKey: string
  ) => {
    if (!answer.documentId)
    {
      addToast('Arquivo não disponível para download.', 'error');
      return;
    }

    try
    {
      setAttachmentActionKey(actionKey);
      await documentDownloadService.downloadAndSaveDocument(
        answer.documentId,
        resolveAttachmentFileName(answer)
      );
    }
    catch
    {
      addToast('Erro ao baixar o arquivo.', 'error');
    }
    finally
    {
      setAttachmentActionKey(null);
    }
  };

  const closeAttachmentViewer = () => {
    setShowDocumentViewer(false);
    setShowOpenDocumentViewer(false);
    setViewerDocument(null);
  };

  const handleViewAttachment = (answer: FormResponseAnswer) => {
    if (!answer.documentId)
    {
      addToast('Arquivo não disponível para visualização.', 'error');
      return;
    }

    const fileName = resolveAttachmentFileName(answer);
    const viewerKind = getAttachmentViewerKind(fileName, answer.contentType);

    if (viewerKind === 'unsupported')
    {
      addToast(
        'Prévia não disponível para este tipo de arquivo. Use a opção Baixar.',
        'info'
      );
      return;
    }

    const documentNode = buildAttachmentDocumentNode(answer);
    setViewerDocument(documentNode);

    if (viewerKind === 'opendocument')
    {
      setShowOpenDocumentViewer(true);
      setShowDocumentViewer(false);
      return;
    }

    setShowDocumentViewer(true);
    setShowOpenDocumentViewer(false);
  };

  if (isLoading || !form) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center text-gray-500">
        Carregando formulário...
      </div>
    );
  }

  const sortedFields = form.fields
    .slice()
    .sort((a, b) => a.order - b.order);
  const sortedSections = sortSections(form.sections);
  const hasMultipleSections = sortedSections.length > 1;
  const resolvedTheme = resolveFormTheme(form.theme);
  const themeCssVars = buildFormThemeCssVars(resolvedTheme);

  const sectionNavLabel = (section: FormSection): string => {
    const index = sortedSections.findIndex((s) => s.id === section.id);
    return `Ir para a seção ${index + 1} (${section.title || 'Sem título'})`;
  };

  const selectField = (field: FormField) => {
    setSelectedFieldId(field.id);
    setActiveSectionId(field.sectionId);
  };

  const renderNavSelect = (
    action: FormNavigationAction,
    targetId: string | null | undefined,
    onChange: (next: FormNavigationAction, target: string | null) => void
  ) => {
    const selectValue =
      action === 'GoToSection' && targetId ? `GoToSection:${targetId}` : action;

    return (
      <select
        value={selectValue}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => {
          const value = e.target.value;
          if (value.startsWith('GoToSection:')) {
            onChange('GoToSection', value.slice('GoToSection:'.length));
            return;
          }
          onChange(value as FormNavigationAction, null);
        }}
        className="max-w-[16rem] rounded-md border border-gray-200 bg-white px-2 py-1.5 text-sm text-gray-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
      >
        <option value="ContinueToNext">Continuar para a próxima seção</option>
        {sortedSections.map((section) => (
          <option key={section.id} value={`GoToSection:${section.id}`}>
            {sectionNavLabel(section)}
          </option>
        ))}
        <option value="SubmitForm">Enviar formulário</option>
      </select>
    );
  };

  const renderSectionBanner = (section: FormSection, sectionIndex: number) => {
    if (!hasMultipleSections) {
      return null;
    }

    return (
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
        <div
          className="px-4 py-1.5 text-xs font-semibold text-white"
          style={{ backgroundColor: resolvedTheme.primaryColor }}
        >
          Seção {sectionIndex + 1} de {sortedSections.length}
        </div>
        <div className="flex items-start gap-2 p-4">
          <div className="flex-1 space-y-2">
            <input
              type="text"
              value={section.title}
              onChange={(e) =>
                handleUpdateSection(section.id, { title: e.target.value })
              }
              onFocus={(e) =>
              {
                setActiveSectionId(section.id);
                e.currentTarget.style.borderColor = resolvedTheme.primaryColor;
              }}
              onBlur={(e) =>
              {
                e.currentTarget.style.borderColor = '';
              }}
              className="w-full rounded-md border border-gray-200 px-3 py-2 font-semibold text-gray-900 focus:outline-none focus:ring-1"
              style={{
                fontSize: resolvedTheme.textSizes.section,
                ['--tw-ring-color' as string]: resolvedTheme.primaryColor,
              }}
              placeholder="Seção sem título"
            />
            <textarea
              value={section.description ?? ''}
              onChange={(e) =>
                handleUpdateSection(section.id, { description: e.target.value })
              }
              onFocus={(e) =>
              {
                setActiveSectionId(section.id);
                e.currentTarget.style.borderColor = resolvedTheme.primaryColor;
              }}
              onBlur={(e) =>
              {
                e.currentTarget.style.borderColor = '';
              }}
              rows={1}
              className="w-full resize-y rounded-md border border-gray-200 px-3 py-2 text-gray-600 focus:outline-none focus:ring-1"
              style={{
                fontSize: resolvedTheme.textSizes.body,
                ['--tw-ring-color' as string]: resolvedTheme.primaryColor,
              }}
              placeholder="Descrição (opcional)"
            />
          </div>
          <button
            type="button"
            title="Excluir seção"
            onClick={() => handleDeleteSection(section.id)}
            className="rounded p-2 text-gray-400 hover:bg-gray-100 hover:text-red-600"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
          </button>
        </div>
      </div>
    );
  };

  const renderAfterSectionControl = (
    section: FormSection,
    sectionIndex: number
  ) => {
    if (!hasMultipleSections) {
      return null;
    }

    return (
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-dashed border-gray-300 bg-white px-4 py-3 text-sm text-gray-600">
        <span className="font-medium text-gray-700">
          Após a seção {sectionIndex + 1}
        </span>
        {renderNavSelect(
          section.afterSectionAction ?? 'ContinueToNext',
          section.afterSectionTargetId,
          (action, target) =>
            handleSectionNavChange(section.id, action, target)
        )}
      </div>
    );
  };

  return (
    <div
      className="-m-6 flex h-[calc(100vh-4rem)] flex-col overflow-hidden"
      style={{ backgroundColor: resolvedTheme.backgroundColor }}
    >
      <div className="z-20 shrink-0 border-b border-gray-200 bg-white">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-4 py-3">
          <div className="flex min-w-0 items-center gap-3">
            <Link
              to="/forms"
              className="text-sm text-gray-600 hover:text-gray-900"
            >
              ← Formulários
            </Link>
            <span className="truncate text-sm font-medium text-gray-800">
              {form.title || 'Formulário sem título'}
            </span>
            <span
              className={`hidden rounded-full border px-2.5 py-0.5 text-xs font-medium sm:inline ${formStatusBadgeClass(
                form.status
              )}`}
            >
              {FORM_STATUS_LABELS[form.status]}
            </span>
            <span
              className={`hidden rounded-full border px-2.5 py-0.5 text-xs font-medium sm:inline ${formPurposeBadgeClass(
                form.purpose || 'General'
              )}`}
              title="Finalidade do formulário"
            >
              {FORM_PURPOSE_LABELS[form.purpose || 'General']}
            </span>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              title="Tema"
              aria-label="Abrir painel de tema"
              aria-pressed={isThemePanelOpen}
              onClick={() => setIsThemePanelOpen((prev) => !prev)}
              className={`inline-flex h-8 w-8 items-center justify-center rounded-md transition-colors ${
                isThemePanelOpen
                  ? 'bg-blue-50 text-blue-700'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
              }`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01"
                />
              </svg>
            </button>
            <button
              type="button"
              title="Visualização"
              aria-label="Abrir visualização"
              aria-pressed={isPreviewModalOpen}
              onClick={() => setIsPreviewModalOpen(true)}
              className={`inline-flex h-8 w-8 items-center justify-center rounded-md transition-colors ${
                isPreviewModalOpen
                  ? 'bg-blue-50 text-blue-700'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
              }`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                />
              </svg>
            </button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleSave}
              disabled={isSaving || form.status === 'Closed'}
            >
              {isSaving ? 'Salvando...' : 'Salvar'}
            </Button>
            {form.status !== 'Published' && form.status !== 'Closed' ? (
              <Button
                variant="success"
                size="sm"
                onClick={handlePublish}
                disabled={isSaving}
              >
                Publicar
              </Button>
            ) : null}
            <Button
              variant="dangerOutline"
              size="sm"
              onClick={handleClose}
              disabled={isSaving || form.status === 'Closed'}
            >
              Encerrar
            </Button>
          </div>
        </div>

        <nav className="mx-auto flex max-w-5xl gap-1 px-4">
          {(
            [
              ['perguntas', 'Perguntas'],
              ['respostas', 'Respostas'],
              ['configuracoes', 'Configurações'],
            ] as const
          ).map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setActiveTab(key)}
              className={`rounded-t-md px-4 py-2.5 text-sm font-medium transition-colors ${
                activeTab === key
                  ? 'border-b-2 border-blue-600 text-blue-700'
                  : 'text-gray-500 hover:text-gray-800'
              }`}
            >
              {label}
              {key === 'respostas' && responses.length > 0
                ? ` (${responses.length})`
                : ''}
            </button>
          ))}
        </nav>
      </div>

      <div className="flex min-h-0 flex-1 overflow-hidden">
        <div className="min-w-0 flex-1 overflow-y-auto">
      {activeTab === 'perguntas' && (
        <div
          className="relative mx-auto max-w-3xl px-4 py-8"
          style={themeCssVars}
        >
          <div className="mb-4 overflow-hidden rounded-lg border border-gray-200 bg-white">
            {resolvedTheme.headerImageUrl ? (
              <img
                src={resolvedTheme.headerImageUrl}
                alt=""
                className="h-44 w-full object-cover"
              />
            ) : null}
            <div className="space-y-3 p-6">
              <div
                className="h-1.5 w-14 rounded-full"
                style={{ backgroundColor: resolvedTheme.primaryColor }}
              />
              <input
                type="text"
                value={form.title}
                onChange={(e) => updateFormLocal({ title: e.target.value })}
                className="w-full rounded-md border border-gray-200 px-3 py-2 font-semibold text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-1"
                style={{
                  fontSize: resolvedTheme.textSizes.title,
                  ['--tw-ring-color' as string]: resolvedTheme.primaryColor,
                }}
                onFocus={(e) =>
                {
                  e.currentTarget.style.borderColor = resolvedTheme.primaryColor;
                }}
                onBlur={(e) =>
                {
                  e.currentTarget.style.borderColor = '';
                }}
                placeholder="Formulário sem título"
              />
              <textarea
                value={form.description ?? ''}
                onChange={(e) =>
                  updateFormLocal({ description: e.target.value })
                }
                rows={2}
                className="w-full resize-y rounded-md border border-gray-200 px-3 py-2 text-gray-600 placeholder:text-gray-400 focus:outline-none focus:ring-1"
                style={{
                  fontSize: resolvedTheme.textSizes.body,
                  ['--tw-ring-color' as string]: resolvedTheme.primaryColor,
                }}
                onFocus={(e) =>
                {
                  e.currentTarget.style.borderColor = resolvedTheme.primaryColor;
                }}
                onBlur={(e) =>
                {
                  e.currentTarget.style.borderColor = '';
                }}
                placeholder="Descrição do formulário"
              />
              <div className="border-t border-gray-100 pt-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
                    Finalidade
                  </span>
                  <span
                    className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-medium ${formPurposeBadgeClass(
                      form.purpose || 'General'
                    )}`}
                  >
                    {FORM_PURPOSE_LABELS[form.purpose || 'General']}
                  </span>
                  {form.status !== 'Closed' ? (
                    <button
                      type="button"
                      onClick={openPurposeModal}
                      disabled={isSavingSettings}
                      className="text-sm font-medium text-blue-700 hover:text-blue-900 disabled:opacity-60"
                    >
                      Alterar
                    </button>
                  ) : null}
                </div>
                {(form.purpose || 'General') === 'Support' ? (
                  <p className="mt-1.5 text-xs text-gray-500">
                    Campos de resposta curta podem vincular ao contato do chat
                    (nome, e-mail ou telefone).
                  </p>
                ) : (
                  <p className="mt-1.5 text-xs text-gray-500">
                    Formulário geral do sistema. Mude para Atendimento se for
                    usado no chat.
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-6">
            {sortedSections.map((section, sectionIndex) => (
              <div key={section.id} className="space-y-3">
                {renderSectionBanner(section, sectionIndex)}
                {getSectionFields(form.fields, section.id).map((field) => {
              const fieldIndex = sortedFields.findIndex((f) => f.id === field.id);
              const isSelected = selectedFieldId === field.id;
              const isDisplayOnly = isDisplayOnlyFieldType(field.type);
              const showOptions =
                !isDisplayOnly && FIELD_TYPES_WITH_OPTIONS.includes(field.type);
              const showBranching =
                !isDisplayOnly &&
                hasMultipleSections &&
                FIELD_TYPES_WITH_BRANCHING.includes(field.type);
              const imageSettings =
                field.type === 'Image' ? resolveImageBlockSettings(field) : null;
              const videoSettings =
                field.type === 'Video' ? resolveVideoBlockSettings(field) : null;
              const videoId = videoSettings?.youtubeUrl
                ? extractYoutubeVideoId(videoSettings.youtubeUrl)
                : null;
              const inlineImage = !isDisplayOnly
                ? resolveInlineImageSettings(field)
                : null;
              const inlineImageUrl = inlineImage
                ? resolveDisplayImageUrl({
                    inlineImageUrl: inlineImage.inlineImageUrl,
                    imageSource: inlineImage.imageSource,
                    externalUrl: inlineImage.externalUrl,
                  })
                : undefined;
              const blockImageUrl = imageSettings
                ? resolveDisplayImageUrl({
                    mediaUrl: imageSettings.mediaUrl,
                    imageSource: imageSettings.imageSource,
                    externalUrl: imageSettings.externalUrl,
                  })
                : undefined;

              return (
                <div key={field.id} className="relative">
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => selectField(field)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        selectField(field);
                      }
                    }}
                    className={`relative rounded-lg border bg-white outline-none transition-colors ${
                      isSelected
                        ? 'shadow-sm'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    style={
                      isSelected
                        ? {
                            borderColor: resolvedTheme.primaryColor,
                            boxShadow: `inset 4px 0 0 0 ${resolvedTheme.primaryColor}`,
                          }
                        : undefined
                    }
                  >
                    <div className="px-5 pb-4 pt-3">
                      <div className="mb-3 flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <span
                            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-gray-100 text-xs font-semibold text-gray-600"
                            aria-hidden
                          >
                            {fieldIndex + 1}
                          </span>
                          {isDisplayOnly ? (
                            <span className="rounded-md border border-gray-200 bg-gray-50 px-2.5 py-1 text-sm text-gray-600">
                              {field.type === 'Image' ? 'Imagem' : 'Vídeo'}
                            </span>
                          ) : (
                            <FormFieldTypeSelect
                              value={field.type}
                              onChange={(type) =>
                                updateField(field.id, { type })
                              }
                            />
                          )}
                        </div>
                        <div className="flex text-gray-400">
                          <button
                            type="button"
                            className="rounded p-1 hover:bg-gray-100 hover:text-gray-600"
                            title="Mover para cima"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMoveField(field.id, -1);
                            }}
                          >
                            ↑
                          </button>
                          <button
                            type="button"
                            className="rounded p-1 hover:bg-gray-100 hover:text-gray-600"
                            title="Mover para baixo"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMoveField(field.id, 1);
                            }}
                          >
                            ↓
                          </button>
                        </div>
                      </div>

                      <div className="flex items-start gap-2">
                        <input
                          type="text"
                          value={field.title}
                          onChange={(e) =>
                            updateField(field.id, { title: e.target.value })
                          }
                          onClick={(e) => e.stopPropagation()}
                          className="w-full rounded-md border border-gray-200 px-3 py-2.5 text-gray-900 focus:outline-none focus:ring-1"
                          style={{
                            fontSize: resolvedTheme.textSizes.question,
                            ['--tw-ring-color' as string]: resolvedTheme.primaryColor,
                          }}
                          onFocus={(e) =>
                          {
                            e.currentTarget.style.borderColor =
                              resolvedTheme.primaryColor;
                          }}
                          onBlur={(e) =>
                          {
                            e.currentTarget.style.borderColor = '';
                          }}
                          placeholder={
                            field.type === 'Image'
                              ? 'Título da imagem'
                              : field.type === 'Video'
                                ? 'Título do vídeo'
                                : 'Pergunta sem título'
                          }
                        />
                        {!isDisplayOnly && (
                          <button
                            type="button"
                            title="Adicionar imagem inline"
                            className="mt-1 shrink-0 rounded-md p-2 text-gray-500 hover:bg-gray-100 hover:text-blue-600"
                            onClick={(e) =>
                            {
                              e.stopPropagation();
                              setImageModalTarget({
                                kind: 'inline',
                                fieldId: field.id,
                              });
                            }}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-5 w-5"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                              />
                            </svg>
                          </button>
                        )}
                      </div>

                      {inlineImageUrl && (
                        <div
                          className={`mt-3 flex ${alignmentClass(inlineImage?.alignment)}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="relative inline-block max-w-full">
                            <img
                              src={inlineImageUrl}
                              alt=""
                              className="max-h-64 max-w-full rounded-md border border-gray-200 object-contain"
                            />
                            <div className="absolute left-2 top-2 flex flex-wrap gap-1">
                              <button
                                type="button"
                                className="rounded-full bg-white/90 px-2 py-1 text-xs text-gray-700 shadow hover:bg-white"
                                onClick={() =>
                                  setImageModalTarget({
                                    kind: 'inline',
                                    fieldId: field.id,
                                  })
                                }
                              >
                                Trocar
                              </button>
                              <button
                                type="button"
                                className="rounded-full bg-white/90 px-2 py-1 text-xs text-gray-700 shadow hover:bg-white"
                                onClick={() =>
                                  updateField(field.id, {
                                    settingsJson: patchInlineImageSettings(
                                      field.settingsJson,
                                      null
                                    ),
                                    inlineImageUrl: undefined,
                                  })
                                }
                              >
                                Remover
                              </button>
                              {(['Left', 'Center', 'Right'] as FormImageAlignment[]).map(
                                (alignment) => (
                                  <button
                                    key={alignment}
                                    type="button"
                                    className={`rounded-full bg-white/90 px-2 py-1 text-xs shadow hover:bg-white ${
                                      inlineImage?.alignment === alignment
                                        ? 'text-blue-600'
                                        : 'text-gray-700'
                                    }`}
                                    onClick={() =>
                                      updateField(field.id, {
                                        settingsJson: patchInlineImageSettings(
                                          field.settingsJson,
                                          {
                                            imageSource: inlineImage?.imageSource,
                                            objectKey: inlineImage?.objectKey,
                                            externalUrl: inlineImage?.externalUrl,
                                            alignment,
                                          }
                                        ),
                                      })
                                    }
                                  >
                                    {alignment === 'Left'
                                      ? 'E'
                                      : alignment === 'Right'
                                        ? 'D'
                                        : 'C'}
                                  </button>
                                )
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {field.type === 'Image' && (
                        <div
                          className={`mt-4 flex ${alignmentClass(imageSettings?.alignment)}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {blockImageUrl ? (
                            <div className="relative inline-block max-w-full">
                              <img
                                src={blockImageUrl}
                                alt={field.title}
                                className="max-h-80 max-w-full rounded-md border border-gray-200 object-contain"
                              />
                              <div className="absolute left-2 top-2 flex flex-wrap gap-1">
                                <button
                                  type="button"
                                  className="rounded-full bg-white/90 px-2 py-1 text-xs text-gray-700 shadow hover:bg-white"
                                  onClick={() =>
                                    setImageModalTarget({
                                      kind: 'block',
                                      fieldId: field.id,
                                    })
                                  }
                                >
                                  Trocar
                                </button>
                                <button
                                  type="button"
                                  className="rounded-full bg-white/90 px-2 py-1 text-xs text-gray-700 shadow hover:bg-white"
                                  onClick={() =>
                                    updateField(field.id, {
                                      settingsJson: undefined,
                                      mediaUrl: undefined,
                                    })
                                  }
                                >
                                  Remover
                                </button>
                                {(
                                  ['Left', 'Center', 'Right'] as FormImageAlignment[]
                                ).map((alignment) => (
                                  <button
                                    key={alignment}
                                    type="button"
                                    className={`rounded-full bg-white/90 px-2 py-1 text-xs shadow hover:bg-white ${
                                      imageSettings?.alignment === alignment
                                        ? 'text-blue-600'
                                        : 'text-gray-700'
                                    }`}
                                    onClick={() =>
                                      updateField(field.id, {
                                        settingsJson: buildImageBlockSettingsJson({
                                          imageSource:
                                            imageSettings?.imageSource ?? 'Upload',
                                          objectKey: imageSettings?.objectKey,
                                          externalUrl: imageSettings?.externalUrl,
                                          alignment,
                                        }),
                                      })
                                    }
                                  >
                                    {alignment === 'Left'
                                      ? 'E'
                                      : alignment === 'Right'
                                        ? 'D'
                                        : 'C'}
                                  </button>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <button
                              type="button"
                              className="w-full rounded-md border border-dashed border-gray-300 px-4 py-10 text-sm text-gray-500 hover:border-blue-400 hover:text-blue-600"
                              onClick={() =>
                                setImageModalTarget({
                                  kind: 'block',
                                  fieldId: field.id,
                                })
                              }
                            >
                              Clique para inserir uma imagem
                            </button>
                          )}
                        </div>
                      )}

                      {field.type === 'Video' && (
                        <div className="mt-4 space-y-3" onClick={(e) => e.stopPropagation()}>
                          {videoId ? (
                            <div className="overflow-hidden rounded-md border border-gray-200 bg-black">
                              <iframe
                                title={field.title || 'Vídeo'}
                                src={buildYoutubeEmbedUrl(videoId)}
                                className="aspect-video w-full"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                              />
                            </div>
                          ) : (
                            <button
                              type="button"
                              className="w-full rounded-md border border-dashed border-gray-300 px-4 py-10 text-sm text-gray-500 hover:border-blue-400 hover:text-blue-600"
                              onClick={() => setVideoModalFieldId(field.id)}
                            >
                              Clique para inserir um vídeo do YouTube
                            </button>
                          )}
                          <button
                            type="button"
                            className="text-sm hover:opacity-80"
                            style={{ color: resolvedTheme.primaryColor }}
                            onClick={() => setVideoModalFieldId(field.id)}
                          >
                            {videoId ? 'Trocar vídeo' : 'Selecionar vídeo'}
                          </button>
                        </div>
                      )}

                      {showOptions && (
                        <div className="mt-4 space-y-2">
                          {field.options
                            .slice()
                            .sort((a, b) => a.order - b.order)
                            .map((option) => (
                              <div
                                key={option.id}
                                className="flex items-center gap-2"
                              >
                                <span className="text-gray-400">
                                  {field.type === 'Checkboxes' ? '☐' : '○'}
                                </span>
                                <input
                                  type="text"
                                  value={option.label}
                                  onClick={(e) => e.stopPropagation()}
                                  onChange={(e) =>
                                    handleOptionLabelChange(
                                      field.id,
                                      option.id,
                                      e.target.value
                                    )
                                  }
                                  className="flex-1 border-0 border-b border-transparent bg-transparent py-1 text-sm focus:border-blue-600 focus:outline-none"
                                />
                                {showBranching &&
                                  field.goToSectionBasedOnAnswer && (
                                    <span onClick={(e) => e.stopPropagation()}>
                                      {renderNavSelect(
                                        option.goToAction ?? 'ContinueToNext',
                                        option.goToTargetSectionId,
                                        (action, target) =>
                                          handleOptionNavChange(
                                            field.id,
                                            option.id,
                                            action,
                                            target
                                          )
                                      )}
                                    </span>
                                  )}
                                <button
                                  type="button"
                                  className="text-gray-400 hover:text-red-500"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveOption(field.id, option.id);
                                  }}
                                  title="Remover opção"
                                >
                                  ×
                                </button>
                              </div>
                            ))}
                          <button
                            type="button"
                            className="text-sm hover:opacity-80"
                            style={{ color: resolvedTheme.primaryColor }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAddOption(field.id);
                            }}
                          >
                            Adicionar opção
                          </button>
                        </div>
                      )}

                      {showBranching && (
                        <label
                          className="mt-4 flex items-center gap-2 text-sm text-gray-600"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            type="button"
                            role="switch"
                            aria-checked={field.goToSectionBasedOnAnswer ?? false}
                            onClick={() =>
                              handleToggleFieldBranching(
                                field.id,
                                !(field.goToSectionBasedOnAnswer ?? false)
                              )
                            }
                            className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${
                              field.goToSectionBasedOnAnswer
                                ? ''
                                : 'bg-gray-300'
                            }`}
                            style={
                              field.goToSectionBasedOnAnswer
                                ? { backgroundColor: resolvedTheme.primaryColor }
                                : undefined
                            }
                          >
                            <span
                              className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${
                                field.goToSectionBasedOnAnswer
                                  ? 'translate-x-4'
                                  : 'translate-x-0.5'
                              }`}
                            />
                          </button>
                          Ir para a seção com base na resposta
                        </label>
                      )}

                      {field.type === 'Rating' && (() => {
                        const rating = resolveRatingSettings(field);

                        return (
                          <div
                            className="mt-4 space-y-4"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="flex flex-wrap items-center gap-3">
                              <select
                                value={rating.scale}
                                aria-label="Quantidade de níveis"
                                onChange={(e) =>
                                  updateField(field.id, {
                                    settingsJson: buildRatingSettingsJson(
                                      Number(e.target.value),
                                      rating.icon,
                                      field.settingsJson
                                    ),
                                  })
                                }
                                className="rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                              >
                                {RATING_SCALE_OPTIONS.map((scale) => (
                                  <option key={scale} value={scale}>
                                    {scale}
                                  </option>
                                ))}
                              </select>

                              <div className="relative">
                                <select
                                  value={rating.icon}
                                  aria-label="Ícone da classificação"
                                  onChange={(e) =>
                                    updateField(field.id, {
                                      settingsJson: buildRatingSettingsJson(
                                        rating.scale,
                                        e.target.value as FormRatingIcon,
                                        field.settingsJson
                                      ),
                                    })
                                  }
                                  className="appearance-none rounded-md border border-gray-200 bg-white py-2 pl-10 pr-8 text-sm text-gray-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                >
                                  {RATING_ICON_OPTIONS.map((option) => (
                                    <option key={option.value} value={option.value}>
                                      {option.label}
                                    </option>
                                  ))}
                                </select>
                                <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-amber-400">
                                  <FormRatingIconGlyph
                                    icon={rating.icon}
                                    filled
                                    className="h-5 w-5"
                                  />
                                </span>
                              </div>
                            </div>

                            <FormRatingScale
                              scale={rating.scale}
                              icon={rating.icon}
                            />
                          </div>
                        );
                      })()}

                      {!showOptions &&
                        field.type !== 'Rating' &&
                        field.type !== 'Image' &&
                        field.type !== 'Video' && (
                        <p className="mt-4 text-sm text-gray-400">
                          {field.type === 'ShortText' && 'Texto de resposta curta'}
                          {field.type === 'Paragraph' && 'Texto de parágrafo longo'}
                          {field.type === 'Date' && 'Seleção de data'}
                          {field.type === 'Time' && 'Seleção de horário'}
                          {field.type === 'FileUpload' && 'Upload de arquivo'}
                        </p>
                      )}

                      {isSelected
                        && field.type === 'ShortText'
                        && (form.purpose || 'General') === 'Support'
                        && (
                        <div
                          className="mt-4"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <label className="mb-1 block text-xs font-medium text-gray-600">
                            Vínculo com o contato
                          </label>
                          <select
                            value={resolveContactBinding(field)}
                            onChange={(e) =>
                              updateField(field.id, {
                                settingsJson: buildContactBindingSettingsJson(
                                  e.target.value as FormFieldContactBinding,
                                  field.settingsJson
                                ),
                              })
                            }
                            className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-500"
                          >
                            {FORM_FIELD_CONTACT_BINDING_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                          <p className="mt-1 text-xs text-gray-500">
                            No chat, preenche nome, e-mail ou telefone do contato.
                          </p>
                        </div>
                      )}

                      {expandedValidationId === field.id && !isDisplayOnly && (
                        <div
                          className="mt-4 space-y-3 rounded-md border border-gray-100 bg-gray-50 p-3"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {field.type === 'ShortText' && (() => {
                            const mask = resolveMaskSettings(field);
                            return (
                              <>
                                <div>
                                  <label className="mb-1 block text-xs font-medium text-gray-600">
                                    Máscara / formatação
                                  </label>
                                  <select
                                    value={mask.kind ?? 'None'}
                                    onChange={(e) =>
                                      updateField(field.id, {
                                        settingsJson: buildMaskSettingsJson(
                                          e.target.value as FormFieldMaskKind,
                                          mask.pattern,
                                          field.settingsJson
                                        ),
                                      })
                                    }
                                    className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                  >
                                    {FORM_FIELD_MASK_OPTIONS.map((option) => (
                                      <option key={option.value} value={option.value}>
                                        {option.label}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                                {mask.kind === 'Custom' && (
                                  <div>
                                    <label className="mb-1 block text-xs font-medium text-gray-600">
                                      Padrão personalizado (9 = dígito, A = letra, * = alfanumérico)
                                    </label>
                                    <input
                                      type="text"
                                      value={mask.pattern ?? ''}
                                      onChange={(e) =>
                                        updateField(field.id, {
                                          settingsJson: buildMaskSettingsJson(
                                            'Custom',
                                            e.target.value,
                                            field.settingsJson
                                          ),
                                        })
                                      }
                                      className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                      placeholder="Ex: 999.999.999-99"
                                    />
                                  </div>
                                )}
                              </>
                            );
                          })()}
                          <div>
                            <label className="mb-1 block text-xs font-medium text-gray-600">
                              Regex de validação
                            </label>
                            <input
                              type="text"
                              value={field.validationRegex ?? ''}
                              onChange={(e) =>
                                updateField(field.id, {
                                  validationRegex: e.target.value,
                                })
                              }
                              className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-500"
                              placeholder="Ex: ^[A-Za-z]+$"
                            />
                          </div>
                          <div>
                            <label className="mb-1 block text-xs font-medium text-gray-600">
                              Mensagem de validação
                            </label>
                            <input
                              type="text"
                              value={field.validationMessage ?? ''}
                              onChange={(e) =>
                                updateField(field.id, {
                                  validationMessage: e.target.value,
                                })
                              }
                              className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-500"
                              placeholder="Formato inválido"
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    {isSelected && (
                      <div className="flex items-center justify-end gap-1 rounded-b-lg border-t border-gray-100 px-4 py-3">
                        <button
                          type="button"
                          title="Duplicar"
                          className="rounded p-2 text-gray-500 hover:bg-gray-100"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDuplicateField(field.id);
                          }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                            />
                          </svg>
                        </button>
                        <button
                          type="button"
                          title="Excluir"
                          className="rounded p-2 text-gray-500 hover:bg-gray-100 hover:text-red-600"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteField(field.id);
                          }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                        </button>
                        <div className="mx-2 h-6 w-px bg-gray-200" />
                        {!isDisplayOnly && (
                        <>
                        <label
                          className="flex items-center gap-2 px-2 text-sm text-gray-600"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Obrigatória
                          <button
                            type="button"
                            role="switch"
                            aria-checked={field.isRequired}
                            onClick={() =>
                              updateField(field.id, {
                                isRequired: !field.isRequired,
                              })
                            }
                            className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${
                              field.isRequired ? '' : 'bg-gray-300'
                            }`}
                            style={
                              field.isRequired
                                ? { backgroundColor: resolvedTheme.primaryColor }
                                : undefined
                            }
                          >
                            <span
                              className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${
                                field.isRequired
                                  ? 'translate-x-4'
                                  : 'translate-x-0.5'
                              }`}
                            />
                          </button>
                        </label>
                        <button
                          type="button"
                          title="Validação"
                          className="rounded p-2 text-gray-500 hover:bg-gray-100"
                          onClick={(e) => {
                            e.stopPropagation();
                            setExpandedValidationId((prev) =>
                              prev === field.id ? null : field.id
                            );
                          }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
                            />
                          </svg>
                        </button>
                        </>
                        )}
                      </div>
                    )}
                  </div>

                  {isSelected && (
                    <div className="absolute -right-14 top-3 hidden flex-col gap-1 rounded-md border border-gray-200 bg-white p-1 shadow-sm lg:flex">
                      <button
                        type="button"
                        title="Adicionar pergunta"
                        onClick={handleAddField}
                        className="flex h-9 w-9 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100 hover:text-blue-600"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 4v16m8-8H4"
                          />
                        </svg>
                      </button>
                      <button
                        type="button"
                        title="Adicionar imagem"
                        onClick={() => handleAddMediaField('Image')}
                        className="flex h-9 w-9 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100 hover:text-blue-600"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                      </button>
                      <button
                        type="button"
                        title="Adicionar vídeo"
                        onClick={() => handleAddMediaField('Video')}
                        className="flex h-9 w-9 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100 hover:text-blue-600"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 10l4.553-2.069A1 1 0 0121 8.868v6.264a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                          />
                        </svg>
                      </button>
                      <button
                        type="button"
                        title="Adicionar seção"
                        onClick={handleAddSection}
                        className="flex h-9 w-9 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100 hover:text-blue-600"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 6h16M4 12h16M4 18h16"
                          />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              );
                })}
                {getSectionFields(form.fields, section.id).length === 0 && (
                  <button
                    type="button"
                    onClick={() => handleAddFieldToSection(section.id)}
                    className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-gray-300 bg-white px-4 py-6 text-sm font-medium text-gray-500 hover:border-blue-400 hover:text-blue-600"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4v16m8-8H4"
                      />
                    </svg>
                    Adicionar pergunta
                  </button>
                )}
                {renderAfterSectionControl(section, sectionIndex)}
              </div>
            ))}
          </div>

          <div className="mt-6 flex justify-center gap-2 lg:hidden">
            <Button variant="primary" onClick={handleAddField}>
              Adicionar pergunta
            </Button>
            <Button variant="secondary" onClick={handleAddSection}>
              Adicionar seção
            </Button>
          </div>
        </div>
      )}

      {activeTab === 'respostas' && (
        <div className="mx-auto max-w-3xl px-4 py-8">
          <div className="mb-4 rounded-lg border border-gray-200 bg-white p-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-3xl font-semibold text-gray-900">
                  {responses.length}
                </p>
                <p className="text-sm text-gray-500">
                  {responses.length === 1 ? 'resposta' : 'respostas'}
                </p>
              </div>
              <Button
                variant="primary"
                onClick={handleExport}
                disabled={isExporting || responses.length === 0}
              >
                {isExporting ? 'Exportando...' : 'Exportar Respostas'}
              </Button>
            </div>
          </div>

          {responses.length === 0 ? (
            <div className="rounded-lg border border-gray-200 bg-white p-8 text-center text-gray-500">
              Ainda não há respostas para este formulário.
            </div>
          ) : (
            <div className="space-y-3">
              {responses.map((response) => (
                <div
                  key={response.id}
                  className="rounded-lg border border-gray-200 bg-white p-5"
                >
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <p className="font-medium text-gray-900">
                      {response.respondentEmail || 'Respondente anônimo'}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatDateTime(response.submittedAt)}
                    </p>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-700">
                    {response.answers.map((answer) => {
                      const field = form.fields.find(
                        (f) => f.id === answer.fieldId
                      );
                      const isFileAnswer =
                        field?.type === 'FileUpload' || !!answer.documentId;
                      const fileName = resolveAttachmentFileName(answer);
                      const downloadKey = `${response.id}-${answer.fieldId}-download`;
                      const isBusy = attachmentActionKey === downloadKey;

                      return (
                        <li key={`${response.id}-${answer.fieldId}`}>
                          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                            <div className="min-w-0 flex-1">
                              <span className="font-medium text-gray-800">
                                {field?.title ?? answer.fieldId}:
                              </span>{' '}
                              {isFileAnswer && answer.documentId ? (
                                <span className="inline-flex items-center gap-1.5 text-gray-600">
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-4 w-4 shrink-0 text-gray-400"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                                    />
                                  </svg>
                                  <span className="break-all">{fileName}</span>
                                </span>
                              ) : field?.type === 'Rating' ? (
                                (() => {
                                  const rating = resolveRatingSettings(field);
                                  const parsedValue = Number.parseInt(
                                    answer.valueText ?? '',
                                    10
                                  );
                                  const ratingValue = Number.isFinite(parsedValue)
                                    ? parsedValue
                                    : 0;

                                  if (ratingValue <= 0) {
                                    return (
                                      <span className="text-gray-600">—</span>
                                    );
                                  }

                                  return (
                                    <span className="inline-flex items-center gap-0.5 align-middle">
                                      {Array.from(
                                        { length: rating.scale },
                                        (_, index) => (
                                          <FormRatingIconGlyph
                                            key={index}
                                            icon={rating.icon}
                                            filled={index < ratingValue}
                                            className={
                                              index < ratingValue
                                                ? 'h-4 w-4 text-amber-500'
                                                : 'h-4 w-4 text-gray-300'
                                            }
                                          />
                                        )
                                      )}
                                      <span className="ml-1.5 text-xs text-gray-500">
                                        {ratingValue}/{rating.scale}
                                      </span>
                                    </span>
                                  );
                                })()
                              ) : (
                                <span className="text-gray-600">
                                  {answer.valueText || '—'}
                                </span>
                              )}
                            </div>
                            {isFileAnswer && answer.documentId ? (
                              <div className="flex shrink-0 items-center gap-2">
                                <Button
                                  variant="secondary"
                                  size="sm"
                                  disabled={isBusy}
                                  title="Visualizar"
                                  onClick={() => handleViewAttachment(answer)}
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-5 w-5"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                    />
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                    />
                                  </svg>
                                </Button>
                                <Button
                                  variant="secondary"
                                  size="sm"
                                  disabled={isBusy}
                                  title="Baixar"
                                  onClick={() =>
                                    handleDownloadAttachment(answer, downloadKey)
                                  }
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-5 w-5"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                                    />
                                  </svg>
                                </Button>
                              </div>
                            ) : null}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'configuracoes' && (
        <div className="mx-auto max-w-3xl px-4 py-8">
          <div className="space-y-6 rounded-lg border border-gray-200 bg-white p-6">
            <div>
              <p className="mb-2 text-sm font-medium text-gray-700">Status</p>
              <span
                className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${formStatusBadgeClass(
                  form.status
                )}`}
              >
                {FORM_STATUS_LABELS[form.status]}
              </span>
            </div>

            <div
              className={
                form.status === 'Closed' ? 'opacity-60' : undefined
              }
            >
              <p className="mb-2 text-sm font-medium text-gray-700">
                Tipo de acesso
              </p>
              {form.status === 'Closed' ? (
                <p className="mb-2 text-xs text-gray-500">
                  Formulário encerrado: o tipo de acesso não pode ser alterado.
                </p>
              ) : null}
              <div
                className="grid grid-cols-1 gap-3 sm:grid-cols-2"
                role="radiogroup"
                aria-label="Tipo de acesso"
                aria-disabled={form.status === 'Closed' || isSavingSettings}
              >
                {ACCESS_TYPE_OPTIONS.map((option) => {
                  const isSelected = form.accessType === option.value;
                  const isDisabled =
                    form.status === 'Closed' || isSavingSettings;

                  return (
                    <button
                      key={option.value}
                      type="button"
                      role="radio"
                      aria-checked={isSelected}
                      disabled={isDisabled}
                      onClick={() => handleAccessTypeChange(option.value)}
                      className={`flex items-start gap-3 rounded-lg border p-4 text-left transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:cursor-not-allowed ${
                        isSelected
                          ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500'
                          : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50 disabled:hover:border-gray-200 disabled:hover:bg-white'
                      }`}
                    >
                      <span
                        className={`mt-0.5 ${
                          isSelected ? 'text-blue-600' : 'text-gray-500'
                        }`}
                      >
                        {option.icon}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span
                          className={`block text-sm font-medium ${
                            isSelected ? 'text-blue-900' : 'text-gray-900'
                          }`}
                        >
                          {option.label}
                        </span>
                        <span className="mt-0.5 block text-xs text-gray-500">
                          {option.description}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>

              <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-md border border-gray-100 bg-gray-50 px-3 py-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-700">
                    Finalidade
                  </p>
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    <span
                      className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-medium ${formPurposeBadgeClass(
                        form.purpose || 'General'
                      )}`}
                    >
                      {FORM_PURPOSE_LABELS[form.purpose || 'General']}
                    </span>
                    <p className="text-xs text-gray-500">
                      Atendimento libera vínculo com o contato e o selo na
                      listagem.
                    </p>
                  </div>
                </div>
                {form.status !== 'Closed' ? (
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={isSavingSettings}
                    onClick={openPurposeModal}
                  >
                    Alterar
                  </Button>
                ) : null}
              </div>
            </div>

            <div
              className={`flex items-center justify-between gap-4 rounded-lg border px-4 py-3 ${
                form.status === 'Closed'
                  ? 'border-gray-100 bg-gray-50'
                  : 'border-gray-200'
              }`}
            >
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-800">
                  Aceitando respostas
                </p>
                <p className="text-xs text-gray-500">
                  {form.status === 'Closed'
                    ? 'Formulário encerrado: não é possível aceitar novas respostas.'
                    : 'Quando desativado, o formulário fica visível mas não recebe novas respostas.'}
                </p>
              </div>
              <Switch
                checked={
                  form.status === 'Closed' ? false : form.acceptingResponses
                }
                disabled={form.status === 'Closed' || isSavingSettings}
                onChange={handleAcceptingResponsesChange}
                className={`${
                  form.status !== 'Closed' && form.acceptingResponses
                    ? 'bg-green-600'
                    : 'bg-gray-200'
                } relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50`}
              >
                <span
                  className={`${
                    form.status !== 'Closed' && form.acceptingResponses
                      ? 'translate-x-6'
                      : 'translate-x-1'
                  } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                />
              </Switch>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Link público
              </label>
              <div className="flex flex-col gap-2 sm:flex-row">
                <input
                  type="text"
                  readOnly
                  value={`${window.location.origin}/forms/public/${form.publicId}`}
                  className="flex-1 rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-700"
                />
                <Button variant="secondary" onClick={handleCopyPublicLink}>
                  Copiar
                </Button>
                <a
                  href={`/forms/public/${form.publicId}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
                >
                  Abrir
                </a>
              </div>
            </div>

            <div className="border-t border-gray-100 pt-6">
              <h3 className="text-sm font-semibold text-gray-900">Respostas</h3>
              <p className="mt-1 text-xs text-gray-500">
                Gerenciar como as respostas são coletadas e protegidas.
              </p>

              <div className="mt-4 space-y-4">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-800">
                      Coletar endereços de e-mail
                    </p>
                    <p className="text-xs text-gray-500">
                      Solicita o e-mail do respondente no preenchimento.
                    </p>
                  </div>
                  <select
                    value={form.collectEmail}
                    disabled={form.status === 'Closed' || isSavingSettings}
                    onChange={(e) =>
                      void handleSettingPatch(
                        { collectEmail: e.target.value as FormCollectEmail },
                        'Coleta de e-mail atualizada.'
                      )
                    }
                    className="rounded-md border border-gray-300 px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {(Object.keys(FORM_COLLECT_EMAIL_LABELS) as FormCollectEmail[]).map(
                      (value) => (
                        <option key={value} value={value}>
                          {FORM_COLLECT_EMAIL_LABELS[value]}
                        </option>
                      )
                    )}
                  </select>
                </div>

                <div
                  className={`flex items-center justify-between gap-4 rounded-lg border px-4 py-3 ${
                    form.collectEmail === 'NotCollect'
                      ? 'border-gray-100 bg-gray-50 opacity-70'
                      : 'border-gray-200'
                  }`}
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-800">
                      Enviar cópia das respostas por e-mail
                    </p>
                    <p className="text-xs text-gray-500">
                      Requer coleta de e-mail ativada.
                    </p>
                  </div>
                  <Switch
                    checked={form.sendResponseCopyToRespondent}
                    disabled={
                      form.status === 'Closed'
                      || isSavingSettings
                      || form.collectEmail === 'NotCollect'
                    }
                    onChange={(checked) =>
                      void handleSettingPatch(
                        { sendResponseCopyToRespondent: checked },
                        'Envio de cópia atualizado.'
                      )
                    }
                    className={`${
                      form.sendResponseCopyToRespondent ? 'bg-green-600' : 'bg-gray-200'
                    } relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50`}
                  >
                    <span
                      className={`${
                        form.sendResponseCopyToRespondent
                          ? 'translate-x-6'
                          : 'translate-x-1'
                      } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                    />
                  </Switch>
                </div>

                <div className="flex items-center justify-between gap-4 rounded-lg border border-gray-200 px-4 py-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-800">
                      Permitir edição das respostas
                    </p>
                    <p className="text-xs text-gray-500">
                      As respostas podem ser alteradas após o envio.
                    </p>
                  </div>
                  <Switch
                    checked={form.allowResponseEditing}
                    disabled={form.status === 'Closed' || isSavingSettings}
                    onChange={(checked) =>
                      void handleSettingPatch(
                        { allowResponseEditing: checked },
                        'Edição de respostas atualizada.'
                      )
                    }
                    className={`${
                      form.allowResponseEditing ? 'bg-green-600' : 'bg-gray-200'
                    } relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50`}
                  >
                    <span
                      className={`${
                        form.allowResponseEditing ? 'translate-x-6' : 'translate-x-1'
                      } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                    />
                  </Switch>
                </div>

                {form.allowResponseEditing ? (
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-800">
                        Prazo para edição (dias)
                      </p>
                      <p className="text-xs text-gray-500">
                        Deixe em branco para sem prazo.
                      </p>
                    </div>
                    <input
                      type="number"
                      min={1}
                      disabled={form.status === 'Closed' || isSavingSettings}
                      value={form.responseEditingDeadlineDays ?? ''}
                      onBlur={(e) => {
                        const raw = e.target.value.trim();
                        if (!raw) {
                          void handleSettingPatch(
                            { responseEditingDeadlineDays: null },
                            'Prazo de edição removido.'
                          );
                          return;
                        }
                        const days = Number(raw);
                        if (!Number.isFinite(days) || days < 1) {
                          return;
                        }
                        void handleSettingPatch(
                          { responseEditingDeadlineDays: Math.floor(days) },
                          'Prazo de edição atualizado.'
                        );
                      }}
                      onChange={(e) => {
                        const raw = e.target.value.trim();
                        updateFormLocal({
                          responseEditingDeadlineDays: raw
                            ? Number(raw)
                            : null,
                        });
                      }}
                      className="w-28 rounded-md border border-gray-300 px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
                      placeholder="Sem prazo"
                    />
                  </div>
                ) : null}

                <div
                  className={`flex items-center justify-between gap-4 rounded-lg border px-4 py-3 ${
                    form.accessType !== 'Private'
                      ? 'border-gray-100 bg-gray-50 opacity-70'
                      : 'border-gray-200'
                  }`}
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-800">
                      Limitar a 1 resposta
                    </p>
                    <p className="text-xs text-gray-500">
                      Requer formulário privado (login necessário).
                    </p>
                  </div>
                  <Switch
                    checked={form.limitOneResponsePerUser}
                    disabled={
                      form.status === 'Closed'
                      || isSavingSettings
                      || form.accessType !== 'Private'
                    }
                    onChange={(checked) =>
                      void handleSettingPatch(
                        { limitOneResponsePerUser: checked },
                        'Limite de respostas atualizado.'
                      )
                    }
                    className={`${
                      form.limitOneResponsePerUser ? 'bg-green-600' : 'bg-gray-200'
                    } relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50`}
                  >
                    <span
                      className={`${
                        form.limitOneResponsePerUser
                          ? 'translate-x-6'
                          : 'translate-x-1'
                      } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                    />
                  </Switch>
                </div>
              </div>
            </div>

            <div className="border-t border-gray-100 pt-6">
              <h3 className="text-sm font-semibold text-gray-900">Apresentação</h3>
              <p className="mt-1 text-xs text-gray-500">
                Gerenciar como o formulário e as respostas são apresentados.
              </p>

              <div className="mt-4 space-y-4">
                <div className="flex items-center justify-between gap-4 rounded-lg border border-gray-200 px-4 py-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-800">
                      Mostrar barra de progresso
                    </p>
                  </div>
                  <Switch
                    checked={form.showProgressBar}
                    disabled={form.status === 'Closed' || isSavingSettings}
                    onChange={(checked) =>
                      void handleSettingPatch(
                        { showProgressBar: checked },
                        'Barra de progresso atualizada.'
                      )
                    }
                    className={`${
                      form.showProgressBar ? 'bg-green-600' : 'bg-gray-200'
                    } relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50`}
                  >
                    <span
                      className={`${
                        form.showProgressBar ? 'translate-x-6' : 'translate-x-1'
                      } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                    />
                  </Switch>
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Mensagem de confirmação
                  </label>
                  <textarea
                    rows={3}
                    disabled={form.status === 'Closed' || isSavingSettings}
                    value={
                      form.confirmationMessage ?? DEFAULT_CONFIRMATION_MESSAGE
                    }
                    onChange={(e) =>
                      updateFormLocal({ confirmationMessage: e.target.value })
                    }
                    onBlur={() =>
                      void handleSettingPatch(
                        {
                          confirmationMessage:
                            form.confirmationMessage?.trim()
                            || DEFAULT_CONFIRMATION_MESSAGE,
                        },
                        'Mensagem de confirmação atualizada.'
                      )
                    }
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
                  />
                </div>

                <div className="flex items-center justify-between gap-4 rounded-lg border border-gray-200 px-4 py-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-800">
                      Mostrar link para enviar outra resposta
                    </p>
                  </div>
                  <Switch
                    checked={form.showSubmitAnotherResponseLink}
                    disabled={form.status === 'Closed' || isSavingSettings}
                    onChange={(checked) =>
                      void handleSettingPatch(
                        { showSubmitAnotherResponseLink: checked },
                        'Link de nova resposta atualizado.'
                      )
                    }
                    className={`${
                      form.showSubmitAnotherResponseLink
                        ? 'bg-green-600'
                        : 'bg-gray-200'
                    } relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50`}
                  >
                    <span
                      className={`${
                        form.showSubmitAnotherResponseLink
                          ? 'translate-x-6'
                          : 'translate-x-1'
                      } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                    />
                  </Switch>
                </div>

                <div className="flex items-center justify-between gap-4 rounded-lg border border-gray-200 px-4 py-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-800">
                      Tornar novas perguntas obrigatórias por padrão
                    </p>
                  </div>
                  <Switch
                    checked={form.fieldsRequiredByDefault}
                    disabled={form.status === 'Closed' || isSavingSettings}
                    onChange={(checked) =>
                      void handleSettingPatch(
                        { fieldsRequiredByDefault: checked },
                        'Padrão de obrigatoriedade atualizado.'
                      )
                    }
                    className={`${
                      form.fieldsRequiredByDefault ? 'bg-green-600' : 'bg-gray-200'
                    } relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50`}
                  >
                    <span
                      className={`${
                        form.fieldsRequiredByDefault
                          ? 'translate-x-6'
                          : 'translate-x-1'
                      } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                    />
                  </Switch>
                </div>

                <div className="flex items-center justify-between gap-4 rounded-lg border border-gray-200 px-4 py-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-800">
                      Permitir salvamento automático (rascunhos)
                    </p>
                    <p className="text-xs text-gray-500">
                      Salva respostas parciais enquanto o participante preenche.
                    </p>
                  </div>
                  <Switch
                    checked={form.allowDraftResponses}
                    disabled={form.status === 'Closed' || isSavingSettings}
                    onChange={(checked) =>
                      void handleSettingPatch(
                        { allowDraftResponses: checked },
                        'Salvamento automático atualizado.'
                      )
                    }
                    className={`${
                      form.allowDraftResponses ? 'bg-green-600' : 'bg-gray-200'
                    } relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50`}
                  >
                    <span
                      className={`${
                        form.allowDraftResponses ? 'translate-x-6' : 'translate-x-1'
                      } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                    />
                  </Switch>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDocumentViewer && viewerDocument && (
        <DocumentViewer
          document={viewerDocument}
          onClose={closeAttachmentViewer}
        />
      )}

      {showOpenDocumentViewer && viewerDocument && (
        <OpenDocumentViewer
          document={viewerDocument}
          onClose={closeAttachmentViewer}
        />
      )}
        </div>

        {isThemePanelOpen ? (
          <aside className="flex w-[min(100%,380px)] shrink-0 flex-col overflow-hidden border-l border-gray-200 bg-white shadow-sm">
            <div className="shrink-0 border-b border-gray-100">
              <div className="flex items-center justify-between px-4 py-3">
                <p className="text-sm font-semibold text-gray-900">Tema</p>
                <button
                  type="button"
                  title="Fechar tema"
                  aria-label="Fechar painel de tema"
                  onClick={() => setIsThemePanelOpen(false)}
                  className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
              <div className="flex flex-wrap items-center gap-2 px-4 pb-3">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={form.status === 'Closed'}
                  onClick={handleRestoreDefaultTheme}
                >
                  Restaurar
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  onClick={handleSave}
                  disabled={isSaving || form.status === 'Closed'}
                >
                  {isSaving ? 'Salvando...' : 'Salvar'}
                </Button>
              </div>
              <p className="px-4 pb-3 text-xs text-gray-500">
                Alterações aparecem em tempo real nas abas do formulário.
              </p>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-4">
              <FormThemeEditor
                theme={resolvedTheme}
                disabled={form.status === 'Closed'}
                isUploadingHeader={isUploadingHeader}
                hideTitle
                onChange={handleThemeChange}
                onUploadHeader={handleUploadThemeHeader}
                onRemoveHeader={handleRemoveThemeHeader}
                onRestoreDefault={handleRestoreDefaultTheme}
              />
            </div>
          </aside>
        ) : null}
      </div>

      <Modal
        isOpen={isPurposeModalOpen}
        title="Alterar finalidade"
        onClose={() =>
        {
          if (!isSavingSettings)
          {
            setIsPurposeModalOpen(false);
          }
        }}
      >
        <p className="mb-3 text-sm text-gray-600">
          A finalidade define recursos do editor. Em Atendimento, campos podem
          vincular ao contato do chat.
        </p>
        <div className="mb-6 space-y-2">
          {FORM_PURPOSE_OPTIONS.map((option) =>
          {
            const selected = purposeDraft === option.value;
            return (
              <button
                key={option.value}
                type="button"
                disabled={isSavingSettings}
                onClick={() => setPurposeDraft(option.value)}
                className={`flex w-full flex-col rounded-lg border px-4 py-3 text-left transition-colors ${
                  selected
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <span
                  className={`text-sm font-medium ${
                    selected ? 'text-blue-900' : 'text-gray-900'
                  }`}
                >
                  {FORM_PURPOSE_LABELS[option.value]}
                </span>
                <span className="mt-0.5 text-xs text-gray-500">
                  {option.description}
                </span>
              </button>
            );
          })}
        </div>
        <div className="flex justify-end gap-2">
          <Button
            variant="secondary"
            disabled={isSavingSettings}
            onClick={() => setIsPurposeModalOpen(false)}
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            disabled={isSavingSettings}
            onClick={() => handlePurposeChange(purposeDraft)}
          >
            {isSavingSettings ? 'Salvando...' : 'Salvar finalidade'}
          </Button>
        </div>
      </Modal>

      <ConfirmModal
        isOpen={pendingPurposeChange !== null}
        title="Remover vínculos com o contato?"
        message="Ao mudar para Geral, os vínculos de campos com o contato (nome, e-mail, telefone) serão removidos. Continuar?"
        confirmText="Alterar e remover vínculos"
        cancelText="Cancelar"
        onConfirm={() =>
        {
          if (pendingPurposeChange)
          {
            void applyPurposeChange(pendingPurposeChange);
          }
        }}
        onCancel={() => setPendingPurposeChange(null)}
      />

      <FormPreviewModal
        isOpen={isPreviewModalOpen}
        form={form}
        onClose={() => setIsPreviewModalOpen(false)}
        onManageSettings={() =>
        {
          setIsPreviewModalOpen(false);
          setActiveTab('configuracoes');
        }}
      />

      <FormInsertImageModal
        isOpen={imageModalTarget !== null}
        formId={form.id}
        onClose={() => setImageModalTarget(null)}
        onConfirm={handleImageModalConfirm}
        uploadImage={(id, file) => formService.uploadFormMediaImage(id, file)}
      />

      <FormSelectVideoModal
        isOpen={videoModalFieldId !== null || isNewVideoModalOpen}
        initialUrl={(() => {
          if (!videoModalFieldId)
          {
            return '';
          }

          const videoField = form.fields.find((f) => f.id === videoModalFieldId);
          if (!videoField)
          {
            return '';
          }

          return resolveVideoBlockSettings(videoField).youtubeUrl ?? '';
        })()}
        onClose={() =>
        {
          setVideoModalFieldId(null);
          setIsNewVideoModalOpen(false);
        }}
        onConfirm={(youtubeUrl) =>
          handleVideoModalConfirm(youtubeUrl, videoModalFieldId)
        }
      />
    </div>
  );
};
